import { useQuery } from "@apollo/client/react";
import {
    FieldLabel,
    Input,
    Mono,
    RadioGroup,
    RadioGroupItem,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Text,
    Textarea,
} from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { PushNotificationCourseOptionsDocument } from "@/graphql/pushNotifications.generated";
import { CourseByIdDocument } from "@/graphql/courses.generated";
import type { NotificationLink, PushNotificationDraft } from "./pushNotificationDraft";

const LINK_OPTIONS: { value: NotificationLink; label: string }[] = [
    { value: "none", label: "None" },
    { value: "chat", label: "Coaching Chat page" },
    { value: "course", label: "Course" },
    { value: "course_step", label: "Course step" },
];

interface PushNotificationComposerProps {
    draft: PushNotificationDraft;
    onChange: (draft: PushNotificationDraft) => void;
    /** The "Notification Link" section. The alumni console hides it: its notifications open the app, nowhere in it. */
    showDeepLinkConfig?: boolean;
}

/**
 * What a push notification says and where it opens: the admin portal's and the alumni console's
 * composer. Who it goes to is the page's own section, since the two choose recipients differently.
 */
export function PushNotificationComposer({
    draft,
    onChange,
    showDeepLinkConfig = true,
}: PushNotificationComposerProps) {
    const { link } = draft;

    // Only fetched for the two link types that actually need a course to pick from.
    const { data: courseOptionsData } = useQuery(PushNotificationCourseOptionsDocument, {
        skip: link !== "course" && link !== "course_step",
    });
    const courseOptions = (courseOptionsData?.courses ?? []).filter((c) => c.status === "PUBLISHED");
    // The course-step picker's second dropdown needs the chosen course's own content list.
    const { data: selectedCourseData } = useQuery(CourseByIdDocument, {
        variables: { id: draft.courseId ?? "" },
        skip: link !== "course_step" || !draft.courseId,
    });
    const contentOptions = selectedCourseData?.course?.content ?? [];

    function handleLinkChange(next: NotificationLink) {
        onChange({ ...draft, link: next, courseId: undefined, contentId: undefined });
    }

    function handleCourseChange(courseId: string) {
        onChange({ ...draft, courseId, contentId: undefined });
    }

    return (
        <>
            {/* Notification content section */}
            <div className="border-border rounded-2xl border">
                <div className="bg-card-background border-border rounded-t-2xl border-b px-5 py-3">
                    <Mono className="text-muted-foreground text-xs tracking-widest uppercase">
                        Notification Context
                    </Mono>
                </div>
                <div className="space-y-4 px-5 py-4">
                    <FieldLabel optional>Internal title</FieldLabel>
                    <Text muted className="text-xs">
                        Short description for tracking the purpose and audience (not shown to users).
                    </Text>
                    <Input
                        placeholder="e.g. Weekly check-in — all users"
                        value={draft.internalTitle}
                        onChange={(e) => onChange({ ...draft, internalTitle: e.target.value })}
                        type="text"
                    />
                    <FieldLabel>Title</FieldLabel>
                    <Input
                        placeholder="Enter notification title"
                        value={draft.title}
                        onChange={(e) => onChange({ ...draft, title: e.target.value })}
                        type="text"
                    />
                    <FieldLabel>Body</FieldLabel>
                    <Textarea
                        placeholder="Enter notification message"
                        className="min-h-28 resize-none"
                        value={draft.body}
                        onChange={(e) => onChange({ ...draft, body: e.target.value })}
                    />
                </div>
            </div>

            {/* Notification link section */}
            {showDeepLinkConfig && (
                <div className="border-border rounded-2xl border">
                    <div className="bg-card-background border-border rounded-t-2xl border-b px-5 py-3">
                        <Mono className="text-muted-foreground text-xs tracking-widest uppercase">
                            Notification Link
                        </Mono>
                    </div>
                    <div className="space-y-3 px-5 py-4">
                        <RadioGroup
                            value={link}
                            onValueChange={(v) => handleLinkChange(v as NotificationLink)}
                            className="flex flex-row flex-wrap gap-2"
                        >
                            {LINK_OPTIONS.map((opt) => (
                                <label
                                    key={opt.value}
                                    className={cn(
                                        "flex cursor-pointer items-center gap-2 rounded-md border px-3.5 py-1.5 text-sm font-medium transition-colors select-none",
                                        link === opt.value
                                            ? "border-secondary bg-secondary/10 text-secondary"
                                            : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                                    )}
                                >
                                    <RadioGroupItem
                                        value={opt.value}
                                        className={cn(
                                            "h-3.5 w-3.5",
                                            link === opt.value
                                                ? "border-secondary text-secondary"
                                                : "border-muted-foreground/50",
                                        )}
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </RadioGroup>
                        {(link === "course" || link === "course_step") && (
                            <Select value={draft.courseId} onValueChange={handleCourseChange}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select a course" />
                                </SelectTrigger>
                                <SelectContent>
                                    {courseOptions.map((course) => (
                                        <SelectItem key={course.id} value={course.id}>
                                            {course.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {link === "course_step" && draft.courseId && (
                            <Select
                                value={draft.contentId}
                                onValueChange={(contentId) => onChange({ ...draft, contentId })}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select a course step" />
                                </SelectTrigger>
                                <SelectContent>
                                    {contentOptions.map((item) => (
                                        <SelectItem key={item.id} value={item.id}>
                                            {item.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
